/* ============================================================
   Farbe rechnen.

   Zwei Aufgaben, und beide kommen aus der Entwurfsmappe:

   1. `mischen(a, b, t)` — der Bogen von der Bürofarbe in die Phasenfarbe
      (E9). Die Karte zeichnet ihn als `line-gradient`, die Farbvorgabenseite
      als CSS-Verlauf, die Legende als einzelne Tupfer. Alle drei brauchen
      dieselbe Zwischenfarbe, sonst sieht derselbe Bogen an drei Stellen
      anders aus. Gerechnet wird in sRGB, genau wie `mix()` in
      .design/lib.mjs — nicht in OKLab: der Entwurf ist in sRGB gemischt
      abgenommen worden, und eine „richtigere" Mischung wäre eine andere.

   2. `lesbar(hex)` — schwarze oder weiße Schrift auf einer Phasenplakette.
      Die Phasenfarben sind hell (LPH 1–9 laufen auf einer Helligkeit), aber
      ein Admin darf umfärben (E10), und dann kann eine dunkle Farbe darunter
      liegen. Entschieden wird über die relative Leuchtdichte nach WCAG 2.1
      (nicht über die naive YIQ-Formel): das ist dieselbe Größe, mit der man
      hinterher den Kontrast nachrechnet.

   Alle Funktionen nehmen „#rrggbb" — so liefert es die Datenbank (types.ts:
   „colours are '#rrggbb'"). Kurzform (#abc) wird mitgenommen, weil sie beim
   Tippen im Verwaltungsformular entsteht; alles andere ergibt einen
   vorsichtigen Rückfall statt einer Ausnahme, denn eine kaputte Farbe darf
   keinen Bildschirm umwerfen.
   ============================================================ */

/** #rgb oder #rrggbb → [r, g, b] in 0…255. Unlesbares ergibt Mittelgrau. */
export function zerlegen(hex: string): [number, number, number] {
  const h = hex.trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{3}$/.test(h)) {
    const r = h[0] ?? "0";
    const g = h[1] ?? "0";
    const b = h[2] ?? "0";
    return [parseInt(r + r, 16), parseInt(g + g, 16), parseInt(b + b, 16)];
  }
  if (/^[0-9a-fA-F]{6}$/.test(h)) {
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  return [128, 128, 128];
}

const zwei = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");

/** [r, g, b] → "#rrggbb". */
export function fassen(rgb: readonly [number, number, number]): string {
  return "#" + zwei(rgb[0]) + zwei(rgb[1]) + zwei(rgb[2]);
}

/**
 * Lineare Mischung in sRGB. `t = 0` ist `a`, `t = 1` ist `b`; Werte
 * außerhalb werden abgeschnitten, damit ein Rechenfehler beim Aufrufer keine
 * Farbe außerhalb des Raums erzeugt.
 */
export function mischen(a: string, b: string, t: number): string {
  const k = Math.max(0, Math.min(1, t));
  const x = zerlegen(a);
  const y = zerlegen(b);
  return fassen([x[0] + (y[0] - x[0]) * k, x[1] + (y[1] - x[1]) * k, x[2] + (y[2] - x[2]) * k]);
}

/**
 * Stützstellen eines Verlaufs — `n` Farben von `a` nach `b`, beide Enden
 * inbegriffen. Der Bogen auf der Karte braucht genau das: MapLibres
 * `line-gradient` will eine Interpolationsliste, keine zwei Endfarben.
 */
export function verlauf(a: string, b: string, n: number): string[] {
  const anzahl = Math.max(2, Math.floor(n));
  return Array.from({ length: anzahl }, (_, i) => mischen(a, b, i / (anzahl - 1)));
}

/** Relative Leuchtdichte nach WCAG 2.1 (0 = schwarz, 1 = weiß). */
export function leuchtdichte(hex: string): number {
  const lin = zerlegen(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/** Kontrastverhältnis zweier Farben, 1…21. */
export function kontrast(a: string, b: string): number {
  const la = leuchtdichte(a);
  const lb = leuchtdichte(b);
  const hell = Math.max(la, lb);
  const dunkel = Math.min(la, lb);
  return (hell + 0.05) / (dunkel + 0.05);
}

/** Das dunkle Grau der Hausschrift (--text im hellen Thema) und reines Weiß. */
const DUNKEL = "#1b1b1c";
const HELL = "#ffffff";

/**
 * Schriftfarbe auf einer Fläche dieser Farbe: das dunkle Textgrau oder Weiß,
 * je nachdem, was mehr Kontrast bringt. Die Entwurfsmappe setzt auf jeder
 * Phasenplakette `#1b1b1c` — bei den dort entworfenen Farben gewinnt genau
 * das auch hier; bei einer nachträglich dunkel gefärbten Phase kippt es auf
 * Weiß, statt unleserlich zu bleiben.
 */
export function lesbar(hex: string): string {
  return kontrast(hex, DUNKEL) >= kontrast(hex, HELL) ? DUNKEL : HELL;
}

/**
 * Dieselbe Farbe mit einer Deckkraft — für Flächen unter einer Linie
 * (Grenzvorschau, Balken der Zeitleiste). `rgba()` und nicht ein achtstelliger
 * Hex-Wert: MapLibre-Stilausdrücke nehmen beides, CSS auch, aber `rgba()`
 * liest sich in den Entwicklerwerkzeugen, und darauf kommt es beim Suchen an.
 */
export function mitDeckkraft(hex: string, alpha: number): string {
  const [r, g, b] = zerlegen(hex);
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
